import dayjs from 'dayjs';
import { getAppRole, getAuthUser } from 'utils';

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Field, Formik } from 'formik';

import moment from 'moment';

import { DatePicker, Select, Spin, Switch } from 'antd';

import { PlusOutlined, PushpinOutlined } from '@ant-design/icons';

import { BtnPrimary } from 'components/shared/Button';
import ModalMessage from 'components/shared/ModalMessage';
import UserIcon from 'components/shared/UserIcon';
import { COLORS } from 'constants/colors';
import { ROLES } from 'constants/global';
import { Community, ICreatePollFormValues } from 'constants/types';
import { useAddPollMutation } from 'services/pollManagement';
import { useGetAllTopicsCategoriesQuery } from 'services/topicCategoryManagement';

import { createPollValidationSchema } from 'utils/validation/schemas';

import {
  Bullet,
  CommunityName,
  ErrorContainer,
  ErrorText,
  PinPostWrapper,
  StyledCategoryLabel,
  StyledFormItem,
  StyledHeader,
  StyledLabel,
  StyledPinButton,
  StyledPostCategory,
  StyledRole,
  StyledSelectWrapper,
  StyledUserDetails,
  StyledUserName,
} from '../CreateTopicModal/styles';
import {
  SettingItem,
  SettingsWrapper,
  StyledCloseIcon,
  StyledFAddOptionButton,
  StyledFormSection,
  StyledInput,
  StyledModal,
  StyledOption,
  StyledSectionLabel,
  StyledSettingsSection,
  StyledSpan,
  StyledTextArea,
} from './styles';
import {
  useGetCommunitiesQuery,
  useGetCommunityQuery,
} from 'oyofrontend/communityManagement';

const { Option } = Select;

interface CreatePollModalProps {
  isVisible: boolean;
  onClose: () => void;
  refetchPinned: () => void;
  refetchUnpinned: () => void;
}

const CreatePollModal: React.FC<CreatePollModalProps> = ({
  isVisible,
  onClose,
  refetchPinned,
  refetchUnpinned,
}) => {
  const { t } = useTranslation('forum');
  const [addPoll] = useAddPollMutation();
  const [submitting, setSubmitting] = useState(false);
  const { data: communities = [] } = useGetCommunitiesQuery();
  const { data: categories = [] } = useGetAllTopicsCategoriesQuery();
  const { data: community } = useGetCommunityQuery();
  const userRole = getAppRole();
  const [isPinned, setIsPinned] = useState(false);
  const [isAllCommunitiesSelected, setIsAllCommunitiesSelected] =
    useState(true);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [triggerClose, setTriggerClose] = useState(false);
  const [isEndDateEnabled, setIsEndDateEnabled] = useState(false);
  const [validateOnChange, setValidateOnChange] = useState(false);

  const handleTopicCategoryChange = (value: string, setFieldValue: any) => {
    setFieldValue('category', value);
  };

  const handleCommunityChange = (
    value: string[] | string,
    setFieldValue: any,
  ) => {
    if (value.includes('all_communities')) {
      setFieldValue(
        'communities',
        communities.map((community: Community) => community.id),
      );
      setIsAllCommunitiesSelected(true);
    } else {
      setFieldValue('communities', value);
      setIsAllCommunitiesSelected(false);
    }
  };

  const handleModalClose = (resetForm: any, dirty: boolean) => {
    if (dirty && !triggerClose) {
      setShowWarningModal(true);
    } else {
      resetForm();
      setIsPinned(false);
      setIsEndDateEnabled(false);
      onClose();
      setTriggerClose(false);
    }
  };

  const handleConfirmClose = (resetForm: any) => {
    setTriggerClose(true);
    handleModalClose(resetForm, false);
    setShowWarningModal(false);
    setIsEndDateEnabled(false);
  };

  const handleOnFocus = (field: string, setErrors: any) => {
    setErrors((prevErrors: any) => ({ ...prevErrors, [field]: undefined }));
  };

  const handleOnSave = (validateForm: any, handleSubmit: any) => {
    setValidateOnChange(true);
    validateForm().then((errors: any) => {
      if (Object.keys(errors).length === 0) {
        handleSubmit();
      }
    });
  };

  const handlePollOptionChange = (
    value: string,
    index: number,
    setFieldValue: any,
    values: ICreatePollFormValues,
  ) => {
    const newOptions = [...values.pollOptions];
    newOptions[index] = value;
    setFieldValue('pollOptions', newOptions);
  };

  const handleAddOption = (
    setFieldValue: any,
    values: ICreatePollFormValues,
  ) => {
    setFieldValue('pollOptions', [...values.pollOptions, '']);
  };

  const handleRemoveOption = (
    index: number,
    setFieldValue: any,
    values: ICreatePollFormValues,
  ) => {
    const newOptions = values.pollOptions.filter((_, i) => i !== index);
    setFieldValue('pollOptions', newOptions);
  };

  const togglePinPost = () => {
    setIsPinned((prev) => !prev);
  };

  const handleSubmit = async (values: ICreatePollFormValues, actions: any) => {
    try {
      setSubmitting(true);

      const communities =
        userRole === ROLES.PMO && community?.id
          ? [community.id]
          : Array.isArray(values.communities)
            ? values.communities
            : [values.communities];

      const payload = {
        ...values,
        pinned: isPinned,
        communities: communities,
      };

      await addPoll(payload).unwrap();

      setIsPinned(false);
      setIsEndDateEnabled(false);
      actions.resetForm();
      refetchPinned();
      refetchUnpinned();
      onClose();
    } catch (error) {
      console.error('Failed to add poll:', error);
    } finally {
      setSubmitting(false);
      actions.setSubmitting(false);
    }
  };

  return (
    <>
      <Formik
        enableReinitialize={true}
        validateOnChange={validateOnChange}
        validateOnBlur={false}
        initialValues={{
          pinned: false,
          questionTitle: '',
          questionDescription: '',
          category: categories[0]?._id || '',
          communities:
            communities.map((community: Community) => community.id) || [],
          pollOptions: ['', ''],
          isAnonymous: false,
          multipleAnswers: false,
          endDate: null,
        }}
        validationSchema={createPollValidationSchema(isEndDateEnabled)}
        onSubmit={handleSubmit}
      >
        {({
          isValid,
          isSubmitting,
          values,
          errors,
          validateForm,
          setFieldValue,
          setErrors,
          handleSubmit,
          resetForm,
          dirty,
          setFieldTouched,
          validateField,
        }) => (
          <>
            <StyledModal
              title={t('create_new_poll')}
              open={isVisible}
              onCancel={() => handleModalClose(resetForm, dirty)}
              footer={null}
            >
              <Spin
                spinning={submitting}
                size="large"
                tip={t('loading')}
                style={{ color: COLORS.Green8 }}
              >
                <StyledHeader>
                  <StyledUserDetails>
                    <UserIcon
                      firstName={getAuthUser().firstName}
                      lastName={getAuthUser().lastName}
                      userRole={getAppRole()}
                    />
                    <div>
                      <StyledUserName>
                        <StyledRole>
                          {userRole === ROLES.PMO ? 'PMO' : 'OYO'}
                        </StyledRole>
                        <Bullet>•</Bullet>
                        <CommunityName>
                          {userRole === ROLES.PMO
                            ? community?.communityName
                            : t('oyo_communitites')}
                        </CommunityName>
                      </StyledUserName>

                      <StyledPostCategory>
                        <StyledCategoryLabel>
                          {t('post_category')}
                        </StyledCategoryLabel>
                        <StyledSelectWrapper>
                          <Select
                            getPopupContainer={(trigger) => trigger.parentNode}
                            onChange={(value) =>
                              handleTopicCategoryChange(value, setFieldValue)
                            }
                            value={values.category}
                          >
                            {categories.map((category) => (
                              <Option key={category._id} value={category._id}>
                                {t(category.title)}
                              </Option>
                            ))}
                          </Select>
                        </StyledSelectWrapper>
                      </StyledPostCategory>
                    </div>
                  </StyledUserDetails>
                  <PinPostWrapper>
                    <StyledPinButton
                      pinned={isPinned}
                      icon={<PushpinOutlined />}
                      onClick={togglePinPost}
                    >
                      {t(isPinned ? 'pinned_post' : 'pin_post')}
                    </StyledPinButton>
                  </PinPostWrapper>
                </StyledHeader>

                {userRole === ROLES.OYO_ADMIN && (
                  <StyledFormItem>
                    <StyledLabel>{t('share_poll_in')}</StyledLabel>
                    <Select
                      mode="multiple"
                      style={{ width: '100%', marginLeft: '6px' }}
                      getPopupContainer={(trigger) => trigger.parentNode}
                      onChange={(value) =>
                        handleCommunityChange(value, setFieldValue)
                      }
                      value={
                        isAllCommunitiesSelected
                          ? 'all_communities'
                          : values.communities
                      }
                      status={errors.communities ? 'error' : undefined}
                      onFocus={() => handleOnFocus('communities', setErrors)}
                    >
                      <Option key="all_communities" value="all_communities">
                        {t('all_communities')}
                      </Option>
                      {communities.map((community: Community) => (
                        <Option key={community.id} value={community.id}>
                          {community.communityName}
                        </Option>
                      ))}
                    </Select>
                  </StyledFormItem>
                )}

                <StyledFormSection>
                  <StyledSectionLabel>{t('question_title')}</StyledSectionLabel>
                  <Field
                    name="questionTitle"
                    as={StyledInput}
                    placeholder={t('voting_on_the_reason')}
                    onBlur={(e: any) => {
                      setFieldTouched('questionTitle', false, false);
                      validateField('questionTitle');
                    }}
                    status={errors.questionTitle ? 'error' : undefined}
                  />
                  {errors.questionTitle && (
                    <ErrorContainer>
                      <ErrorText>{errors.questionTitle}</ErrorText>
                    </ErrorContainer>
                  )}
                </StyledFormSection>

                <StyledFormSection>
                  <StyledSectionLabel>
                    {t('question_description')}
                  </StyledSectionLabel>
                  <Field
                    name="questionDescription"
                    as={StyledTextArea}
                    placeholder={t('voting_on_the_reason')}
                    onBlur={(e: any) => {
                      setFieldTouched('questionDescription', false, false);
                      validateField('questionDescription');
                    }}
                    status={errors.questionDescription ? 'error' : undefined}
                  />
                  {errors.questionDescription && (
                    <ErrorContainer>
                      <ErrorText>{errors.questionDescription}</ErrorText>
                    </ErrorContainer>
                  )}
                </StyledFormSection>

                <StyledFormSection>
                  <StyledSectionLabel>{t('poll_options')}</StyledSectionLabel>
                  {values.pollOptions.map((option, index) => (
                    <StyledOption key={index}>
                      <StyledInput
                        value={option}
                        onChange={(e) =>
                          handlePollOptionChange(
                            e.target.value,
                            index,
                            setFieldValue,
                            values,
                          )
                        }
                        placeholder={t('option_tab', {
                          number: index + 1,
                        })}
                        status={
                          errors.pollOptions?.[index] ? 'error' : undefined
                        }
                      />
                      {errors.pollOptions?.[index] && (
                        <ErrorContainer>
                          <ErrorText>{errors.pollOptions[index]}</ErrorText>
                        </ErrorContainer>
                      )}
                      {values.pollOptions.length > 2 && (
                        <StyledCloseIcon
                          onClick={() =>
                            handleRemoveOption(index, setFieldValue, values)
                          }
                        />
                      )}
                    </StyledOption>
                  ))}
                  <StyledFAddOptionButton
                    onClick={() => handleAddOption(setFieldValue, values)}
                    block
                  >
                    <PlusOutlined /> {t('add_an_option')}
                  </StyledFAddOptionButton>
                  {typeof errors.pollOptions === 'string' && (
                    <ErrorContainer>
                      <ErrorText>{errors.pollOptions}</ErrorText>
                    </ErrorContainer>
                  )}
                </StyledFormSection>

                <StyledSettingsSection>
                  <StyledSectionLabel>{t('settings')}</StyledSectionLabel>
                  <SettingsWrapper>
                    <SettingItem>
                      <Switch
                        checked={values.isAnonymous}
                        onChange={() =>
                          setFieldValue('isAnonymous', !values.isAnonymous)
                        }
                      />
                      <StyledSpan>{t('anonymous_voting')}</StyledSpan>
                    </SettingItem>
                    <SettingItem>
                      <Switch
                        checked={values.multipleAnswers}
                        onChange={() =>
                          setFieldValue(
                            'multipleAnswers',
                            !values.multipleAnswers,
                          )
                        }
                      />
                      <StyledSpan>{t('multiple_answers')}</StyledSpan>
                    </SettingItem>
                  </SettingsWrapper>

                  <SettingsWrapper>
                    <Switch
                      checked={isEndDateEnabled}
                      onChange={(checked) => {
                        setIsEndDateEnabled(checked);
                        if (!checked) {
                          setFieldValue('endDate', null);
                        }
                      }}
                    />
                    <StyledSpan>{t('end_date')}</StyledSpan>
                    <DatePicker
                      style={{ width: '280px' }}
                      placeholder={t('select_date')}
                      getPopupContainer={(trigger) =>
                        trigger.parentNode as HTMLElement
                      }
                      value={
                        values.endDate
                          ? dayjs(values.endDate, 'YYYY-MM-DD')
                          : null
                      }
                      onChange={(date) => {
                        if (date) {
                          setFieldValue('endDate', date.format('YYYY-MM-DD'));
                        } else {
                          setFieldValue('endDate', null);
                        }
                      }}
                      disabled={!isEndDateEnabled}
                      onBlur={() => {
                        setFieldTouched('endDate', true);
                        validateField('endDate');
                      }}
                      status={errors.endDate ? 'error' : undefined}
                    />
                  </SettingsWrapper>
                  {errors.endDate && (
                    <ErrorContainer>
                      <ErrorText>{errors.endDate}</ErrorText>
                    </ErrorContainer>
                  )}
                </StyledSettingsSection>

                <BtnPrimary
                  isDisabled={!isValid || isSubmitting || !dirty}
                  onClick={() => handleOnSave(validateForm, handleSubmit)}
                  additionalStyles={{ width: '100%', height: '32px' }}
                >
                  {t('post')}
                </BtnPrimary>
              </Spin>
            </StyledModal>
            {showWarningModal && (
              <ModalMessage
                messageType="warining"
                title={t('unsaved_changes')}
                subTitle={t('changes_not_saved_warning')}
                btnText={t('discard')}
                handleBtn={() => handleConfirmClose(resetForm)}
                onCancelClick={() => setShowWarningModal(false)}
                btnWidth="82px"
              />
            )}
          </>
        )}
      </Formik>
    </>
  );
};

export default CreatePollModal;
